import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {sanitizeName} from 'util/FileUtil';

/**
 * Returns the filename of the exported table CSV.
 *
 * NOTE: Keep in sync with generate_csv_table_export_filename in benchling/notebook/structured_tables/export/csv.py
 */
function generateCsvTableExportFilename(
  tableName: string,
  tableId: string | null,
  documentId: string | null = null
): string {
  const filename = documentId
    ? `${sanitizeName(tableName)} (${documentId}, ${tableId})`
    : tableId
      ? `${sanitizeName(tableName)} (${tableId})`
      : sanitizeName(tableName);
  return `${sanitizeName(filename)}.csv`;
}

interface Props {
  /**
   * Un-escaped name of the table.
   */
  tableName: string;
  /**
   * API ID for structured tables, NotePart provenance ID for plain tables.
   */
  tableId: string | null;
  /**
   * API ID of the document containing the table, or null if the table is not in a document.
   */
  fileId: string | null;
  /**
   * Whether the table is over the inline row limit.
   */
  isOverInlineRowLimit: boolean;
  /**
   * Whether the table is over the inline cell limit.
   */
  isOverInlineCellLimit: boolean;
}

function getErrorMessage(isOverInlineRowLimit: boolean, isOverInlineCellLimit: boolean): string | null {
  let errorMessage: string | null = null;
  if (isOverInlineRowLimit) {
    const rowLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_ROW_LIMIT;
    assert(rowLimit > 0, `ELN_INLINE_TABLE_VIEW_ROW_LIMIT is unexpectedly negative: ${rowLimit}`);
    errorMessage = `${rowLimit} rows`;
  } else if (isOverInlineCellLimit) {
    const cellLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_CELL_LIMIT;
    assert(cellLimit > 0, `ELN_INLINE_TABLE_VIEW_CELL_LIMIT is unexpectedly negative: ${cellLimit}`);
    errorMessage = `${cellLimit} cells`;
  } else {
    assert(false, 'Neither row nor cell limit was exceeded');
  }
  return errorMessage;
}

/**
 * Renders a small banner to indicate why the table has been force collapsed in a readonly view when exporting or printing.
 */
export function TableReadonlyViewCollapseBanner({
  tableName,
  tableId,
  fileId,
  isOverInlineRowLimit,
  isOverInlineCellLimit,
}: Props): JSX.Element | null {
  const {isExportView, isPrintPreviewView} = EditorPropertiesContext.useContext();
  const errorMessage = getErrorMessage(isOverInlineRowLimit, isOverInlineCellLimit);

  return isExportView ? (
    // Exports include the CSV of the table in the zip, so point the user to the table's CSV file.
    <Banner>
      All tables with over {errorMessage} are displayed in a collapsed format and exported as CSV files.
      <br />
      This table was exported as: <code>{generateCsvTableExportFilename(tableName, tableId, fileId)}</code>.
    </Banner>
  ) : isPrintPreviewView ? (
    // Print previews do not include the CSV of the table, so only show the row limit message.
    <Banner>
      All tables with over {errorMessage} are displayed in a collapsed format.
      <br />
      For full data access, please download the table as a CSV or export the entry.
    </Banner>
  ) : // Do not show the banner when not printing or exporting.
  null;
}

function Banner({children}: {children: React.ReactNode}): JSX.Element {
  return (
    // TODO(BNCH-75467): Replace inline style with Aphrodite once Aphrodite styles are passed to exports
    <div
      style={{
        borderLeft: `4px solid ${BLUE_LIGHT}`,
        marginLeft: 12,
        marginTop: 4,
        paddingLeft: 4,
        maxWidth: '100vw', // necessary for wrapping long CSV file names
      }}
      {...getTestProps({component: 'TableReadonlyViewCollapseBanner', element: 'root'})}
    >
      {children}
    </div>
  );
}
